import { useMutation } from "@apollo/client/react";
import Block from "domain/Block";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import useFetchShiftInstances from "hooks/apollo/shiftInstance/useFetchShiftInstances";
import { useNotificationState } from "hooks/state";
import flatten from "lodash/flatten";
import values from "lodash/values";
import GqlError from "utils/GqlError";
import TimeZone from "utils/TimeZone";
import { RescheduleBlocks } from "./block.gql";
import useBlockFactory from "./useBlockFactory";

export default function useRescheduleBlocks() {
  const { refetch: refetchShiftInstances } = useFetchShiftInstances();
  const blockFactory = useBlockFactory();
  const { setNotification } = useNotificationState();
  const [rescheduleBlocks] = useMutation(RescheduleBlocks, {
    onCompleted: () => {
      refetchShiftInstances();
    },
    onError: (error) => {
      GqlError.handler(error, (errors) => {
        const allErrors = flatten(values(errors)).join("\n");

        setNotification({
          message: allErrors,
          title: "Unable to reschedule block(s)",
          type: "danger",
        });
      });
    },
  });
  const applicationContext = useApplicationContext();

  return (blocks) => {
    const blocksToSchedule = blocks.map((block) => blockFactory(block));

    const workBlocks = blocksToSchedule.filter((block) => Block.isWorkBlock(block));
    const downtimeBlocks = blocksToSchedule.filter((block) => Block.isDowntime(block));

    const formattedWorkBlocks = workBlocks.map((workBlock) => ({
      efficiencyOverride: workBlock.efficiencyOverride,
      id: workBlock.id,
      laborOverride: workBlock.laborOverride,
      lineId: workBlock.line.id,
      lockedOnSchedule: workBlock.lockedOnSchedule,
      performanceOverride: workBlock.performanceOverride,
      productionRateOverride: workBlock.productionRateOverride,
      teardownTimeDuration: workBlock.teardownTimeDuration,
      teardownTimeLabel: workBlock.teardownTimeLabel,
      workOrderId: workBlock.workOrder.id,
      workTimeEndsAt: TimeZone.overrideZone(workBlock.workTimeEndsAt, applicationContext.timeZone.name),
      workTimeStartsAt: TimeZone.overrideZone(workBlock.workTimeStartsAt, applicationContext.timeZone.name),
    }));
    const formattedDowntimeBlocks = downtimeBlocks.map((downtimeBlock) => ({
      endsAt: TimeZone.overrideZone(downtimeBlock.endsAt, applicationContext.timeZone.name),
      id: downtimeBlock.id,
      lineId: downtimeBlock.line.id,
      lockedOnSchedule: downtimeBlock.lockedOnSchedule,
      reason: downtimeBlock.reason,
      reasonCode: downtimeBlock.reasonCode,
      startsAt: TimeZone.overrideZone(downtimeBlock.startsAt, applicationContext.timeZone.name),
    }));

    return rescheduleBlocks({
      optimisticResponse: {
        rescheduleBlocks: {
          __typename: "RescheduleBlocksPayload",
          downtimeBlocks,
          workBlocks,
        },
      },
      variables: {
        downtimeBlocks: formattedDowntimeBlocks,
        workBlocks: formattedWorkBlocks,
      },
    });
  };
}
